import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Check } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { cn } from '@repo/ui/src/lib/utils';
import { useStudents } from '../../../hooks/useStudents';
import { formatVnd } from '../../../lib/formatVnd';
import { useDebouncedValue } from './useDebouncedValue';
import type { SourceTabProps } from './types';
import type { DraftMemberRow } from './confirmRows';

/** studentId -> name, captured at the moment of selection — NOT re-derived
 *  from the latest search results at Tiếp tục time (a real bug caught in
 *  QA: the result list keeps changing as the teacher searches for the next
 *  student, so re-deriving from the current hits silently blanked the name
 *  of anyone picked from an EARLIER search once its results scrolled off). */
export type PickSelection = Map<string, string>;

// eslint-disable-next-line react-refresh/only-export-components -- shared factory, not a component
export function initialPickSelection(): PickSelection {
  return new Map();
}

interface PickTabProps extends SourceTabProps {
  selected: PickSelection;
  onSelectedChange: (next: PickSelection) => void;
}

/** Fix round 1 (item 2): `selected` is CONTROLLED by `AddMembersDialog`
 *  (`selected`/`onSelectedChange`) instead of local `useState` — switching
 *  tabs, or "Quay lại" from the confirm table, keeps every pick. The search
 *  box text itself stays local: it is not "work" the teacher would expect
 *  restored, only the picks are. */
export function PickTab({ klass, existingBySubject, selected, onSelectedChange, onContinue, onCancel }: PickTabProps) {
  const { t } = useTranslation('scheduling');
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query.trim(), 300);

  const { data } = useStudents({ search: debounced, pageSize: 50 }, { enabled: debounced.length > 0 });
  const items = data?.items ?? [];

  const inClassIds = useMemo(
    () => new Set(klass.roster.map((r) => r.studentId)),
    [klass.roster],
  );

  const toggle = (studentId: string, name: string) => {
    if (inClassIds.has(studentId)) return;
    const next = new Map(selected);
    if (next.has(studentId)) next.delete(studentId); else next.set(studentId, name);
    onSelectedChange(next);
  };

  const handleContinue = () => {
    const drafts: DraftMemberRow[] = [...selected.entries()].map(([studentId, studentName]) => ({
      key: studentId,
      studentId,
      studentName,
      newStudent: null,
      existingEnrollment: existingBySubject.get(studentId) ?? null,
      rate: '',
      repriceRequested: false,
      slotIds: null,
    }));
    onContinue(drafts);
  };

  return (
    // Fix round 1 (item 1): the search box + result list scroll together;
    // the footer is a `shrink-0` sibling pinned to the bottom.
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3">
        <div className="flex items-center gap-2 h-9 px-2.5 rounded-lg border border-surface-200 bg-surface-0 shrink-0">
          <Search className="h-[15px] w-[15px] text-surface-400 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('addMembers.pick.searchPlaceholder')}
            className="flex-1 min-w-0 bg-transparent text-sm outline-none"
          />
          {debounced.length > 0 && (
            <span className="text-xs text-surface-400 whitespace-nowrap">
              {t('addMembers.pick.resultCount', { count: items.length })}
            </span>
          )}
        </div>

        <div className="rounded-lg border border-surface-200">
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-surface-400">
              {debounced.length === 0 ? t('addMembers.pick.emptyPrompt') : t('addMembers.pick.noResults')}
            </div>
          ) : (
            items.map((hit) => {
              const inClass = inClassIds.has(hit.id);
              const enr = existingBySubject.get(hit.id);
              const checked = selected.has(hit.id);
              return (
                <button
                  key={hit.id}
                  type="button"
                  disabled={inClass}
                  onClick={() => toggle(hit.id, hit.name)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm border-b border-surface-100 last:border-b-0',
                    inClass ? 'cursor-default opacity-50' : 'cursor-pointer hover:bg-surface-50',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      checked ? 'bg-brand-500 border-brand-500 text-white' : 'border-surface-300',
                    )}
                  >
                    {checked && <Check className="h-[11px] w-[11px]" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="font-medium truncate">{hit.name}</span>
                    {hit.client?.name && <span className="text-xs text-surface-400 truncate">· {hit.client.name}</span>}
                  </span>
                  <span className="text-xs whitespace-nowrap shrink-0">
                    {inClass ? (
                      <span className="text-surface-400">{t('addMembers.pick.alreadyInClass', { name: klass.displayName })}</span>
                    ) : enr ? (
                      <span className="text-brand-700">
                        {enr.paused
                          ? t('addMembers.pick.pausedEnrollment', { subject: klass.subject.name })
                          : t('addMembers.pick.hasEnrollment', {
                            subject: klass.subject.name,
                            rate: formatVnd(enr.rate),
                            unit: enr.pricingType === 'MONTHLY_FLAT' ? t('addMembers.unit.month') : t('addMembers.unit.session'),
                          })}
                      </span>
                    ) : (
                      <span className="text-surface-400">{t('addMembers.pick.noEnrollment', { subject: klass.subject.name })}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="shrink-0 flex items-center justify-between gap-2 border-t border-surface-200 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
        <span className="text-[13px] text-surface-600">{t('addMembers.pick.selectedCount', { count: selected.size })}</span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>{t('addMembers.cancel')}</Button>
          <Button type="button" disabled={selected.size === 0} onClick={handleContinue}>{t('addMembers.continue')}</Button>
        </div>
      </div>
    </div>
  );
}
